<template>
    <div class="Fileupload" :class="{ 'File-uploading': isUploading, 'error': statusCode !== 200 }">
        <TransitionGroup v-if="uploadedFiles.length" tag="div" class="File-upload-list" name="fade" mode="out-in">
            <FileProgress v-for="file in uploadedFiles" :key="file.id" v-bind="file" />
        </TransitionGroup>

        <label
            class="File-upload-zone"
            :for="'FileUpload_'+uid"
            v-bind="scope.file.attributes"
            @drop.prevent="onDrop"
            @dragenter.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
            @dragover.prevent
        >
            <slot name="dropzone" :statusCode="statusCode" :isUploading="isUploading">
                <template v-if="isUploading">
                    <Loader /> Uploading...
                </template>
                <template v-else-if="statusCode !== 200">
                    <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.20164 18.4695L10.1643 4.00506C10.9021 2.66498 13.0979 2.66498 13.8357 4.00506L21.7984 18.4695C22.4443 19.6428 21.4598 21 19.9627 21H4.0373C2.54022 21 1.55571 19.6428 2.20164 18.4695Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12 9V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12 17.0195V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>

                    <p class="File-main-text" style="text-align: center;">
                        <template v-if="statusCode === 413">
                            <span>File is too large</span>
                        </template>
                        <template v-else>
                            <span>Error</span> while uploading
                        </template>
                    </p>

                    <p class="File-formats-text">Status code: {{statusCode}}</p>
                </template>
                <template v-else>
                    <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path d="M288 109.3V352c0 17.7-14.3 32-32 32s-32-14.3-32-32V109.3l-73.4 73.4c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l128-128c12.5-12.5 32.8-12.5 45.3 0l128 128c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L288 109.3zM64 352H192c0 35.3 28.7 64 64 64s64-28.7 64-64H448c35.3 0 64 28.7 64 64v32c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V416c0-35.3 28.7-64 64-64zM432 456a24 24 0 1 0 0-48 24 24 0 1 0 0 48z"/>
                    </svg>

                    <p class="File-main-text">
                        <span>Click to upload</span> or drag and drop
                    </p>

                    <p class="File-formats-text">
                        <slot name="formats">
                            SVG, PNG, JPG or GIF (MAX. 800x400px)
                        </slot>
                    </p>
                </template>
            </slot>

            <input :id="'FileUpload_'+uid" v-bind="scope.file.input.attributes" v-on="scope.file.input.listeners" />
        </label>
    </div>
</template>

<script>
import props    from './utils/props.js';
import emits    from './utils/emits.js';
import watches  from './utils/watches.js';
import methods  from './utils/methods.js';
import computed from './utils/computed.js';

import FileProgress from './FileProgress.vue';

export default
{
    name            : "File",
    inheritAttrs    : true,
    mixins          : [ props, computed, methods, emits, watches ],
    components      : { FileProgress },

    data()
    {
        return (
        {
            statusCode    : 200,
            isUploading   : false,
            dragEnter     : false,
            uploadedFiles : []
        })
    }
}
</script>

<style lang="scss">
.fade-enter-active,
.fade-leave-active
{
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to
{
    opacity: 0;
}

.Fileupload
{
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: px(12);

    user-select: none;

    overflow: hidden;

    &.error
    {
        .File-upload-zone
        {
            color: var(--input-error-text-color);
            background-color: var(--input-error-background-color);
            border: 1px solid var(--input-error-border-color);

            > svg
            {
                color: var(--input-error-text-color);
            }

            p.File-main-text
            {
                > span
                {
                    color: var(--input-error-text-color);
                }
            }
        }
    }

    &:disabled,
    &.disabled
    {
        cursor: not-allowed;

        label.File-upload-zone
        {
            pointer-events: none;
        }
    }

    .File-upload-list
    {
        position: relative;
    }

    label.File-upload-zone
    {
        position: relative;

        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        padding: px(12);

        background: var(--file-background-color);
        border: var(--file-border);

        border-radius: 4px;

        cursor: pointer;

        &.File-uploading
        {
            @include font-body-sm-regular;

            flex-direction: row;
            flex-wrap: nowrap;
            align-items: center;
            justify-content: center;
            gap: px(4);

            opacity: 0.5;
            cursor: wait;
            pointer-events: none;

            svg
            {
                width: px(24);
                height: px(24);

                margin: 0;
            }
        }

        > *
        {
            pointer-events: none;
        }

        > input
        {
            display: none;
        }

        > svg
        {
            width: px(48);
            height: px(48);

            margin-bottom: px(12);

            color: var(--file-icon-color);
        }

        > p.File-main-text
        {
            @include font-body-sm-regular;

            color: var(--file-text-color);

            margin-bottom: px(8);

            > span
            {
                @include font-bold;

                color: var(--file-highlight-color);

                opacity: var(--file-highlight-opacity);
            }
        }

        > p.File-formats-text
        {
            @include font-body-xs-regular;

            color: var(--file-text-color);

            margin-bottom: 0;
        }
    }
}
</style>